import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { Avatar, Pill } from '../components/UI';
import { Search, SlidersHorizontal, ChevronRight, X, Check, Users, Circle } from 'lucide-react';
import { UserProfile } from '../components/UserProfile';

type FilterState = {
  onlineOnly: boolean;
  hasTickets: boolean;
  tags: string[];
};

const FilterDropdown = ({ filter, setFilter, allTags, onClose }: {
  filter: FilterState;
  setFilter: (f: FilterState) => void;
  allTags: string[];
  onClose: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const toggleTag = (tag: string) => {
    const tags = filter.tags.includes(tag)
      ? filter.tags.filter(t => t !== tag)
      : [...filter.tags, tag];
    setFilter({ ...filter, tags });
  };

  const hasActive = filter.onlineOnly || filter.hasTickets || filter.tags.length > 0;

  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 bg-white/95 backdrop-blur-2xl rounded-[16px] shadow-[0_8px_40px_rgba(0,0,0,0.14)] border border-black/8 w-64 z-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
        <span className="text-[15px] font-semibold text-black">Фильтры</span>
        {hasActive && (
          <button
            onClick={() => setFilter({ onlineOnly: false, hasTickets: false, tags: [] })}
            className="text-[13px] text-[#FF3B30] font-medium hover:opacity-70"
          >
            Сбросить
          </button>
        )}
      </div>

      <div className="p-3 space-y-1">
        <button
          onClick={() => setFilter({ ...filter, onlineOnly: !filter.onlineOnly })}
          className="w-full flex items-center justify-between p-3 rounded-[10px] hover:bg-[#F2F2F7] transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-2 h-2 rounded-full bg-[#34C759]" />
            <span className="text-[15px] text-black">Только онлайн</span>
          </div>
          {filter.onlineOnly && <Check size={16} className="text-[#007AFF]" strokeWidth={2.5} />}
        </button>

        <button
          onClick={() => setFilter({ ...filter, hasTickets: !filter.hasTickets })}
          className="w-full flex items-center justify-between p-3 rounded-[10px] hover:bg-[#F2F2F7] transition-colors"
        >
          <span className="text-[15px] text-black">Есть тикеты</span>
          {filter.hasTickets && <Check size={16} className="text-[#007AFF]" strokeWidth={2.5} />}
        </button>
      </div>

      {allTags.length > 0 && (
        <div className="px-3 pb-3">
          <div className="text-[11px] font-bold text-[#8E8E93] uppercase tracking-widest mb-2 px-1">По тегу</div>
          <div className="flex flex-wrap gap-1.5">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`text-[13px] px-2.5 py-1 rounded-full transition-all border ${
                  filter.tags.includes(tag)
                    ? 'bg-[#007AFF] text-white border-[#007AFF]'
                    : 'bg-[#F2F2F7] text-black border-transparent hover:bg-black/10'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const UsersScreen = () => {
  const { users, tickets } = useStore();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterState>({ onlineOnly: false, hasTickets: false, tags: [] });
  const [showFilter, setShowFilter] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const allTags = Array.from(new Set(users.flatMap(u => u.tags))).filter(Boolean);
  const onlineCount = users.filter(u => u.isOnline).length;
  const activeFilterCount = (filter.onlineOnly ? 1 : 0) + (filter.hasTickets ? 1 : 0) + filter.tags.length;

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q) ||
      u.telegramId.includes(q);

    const matchOnline = !filter.onlineOnly || u.isOnline;
    const matchTickets = !filter.hasTickets || u.totalTickets > 0;
    const matchTags = filter.tags.length === 0 || filter.tags.some(t => u.tags.includes(t));

    return matchSearch && matchOnline && matchTickets && matchTags;
  });

  return (
    <div className="flex-1 h-full bg-[#F5F5F7] flex flex-col mobile-content-safe overflow-hidden">
      {/* Header */}
      <div className="px-4 py-5 bg-[#F5F5F7]/90 backdrop-blur-2xl sticky top-0 z-10 border-b border-black/5 shrink-0">
        <div className="flex justify-between items-center mb-4 max-w-4xl mx-auto w-full">
          <div className="flex items-center gap-3">
            <h2 className="text-[34px] font-bold text-black tracking-tight">Пользователи</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-white border border-black/5 shadow-sm rounded-full px-3 py-1.5">
              <span className="w-2 h-2 rounded-full bg-[#34C759]" />
              <span className="text-[13px] font-medium text-black">{onlineCount} онлайн</span>
            </div>
            <span className="bg-black/5 text-[#8E8E93] px-3 py-1.5 rounded-full text-[14px] font-bold">
              {users.length}
            </span>
          </div>
        </div>

        <div className="flex gap-3 max-w-4xl mx-auto w-full">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8E93]" strokeWidth={2} />
            <input
              type="text"
              placeholder="Поиск по имени, @username, ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-white border border-black/5 shadow-sm outline-none rounded-[12px] py-2.5 pl-10 pr-10 text-[16px] focus:ring-2 focus:ring-[#007AFF]/40 transition-all placeholder:text-[#8E8E93]"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 bg-[#C7C7CC] rounded-full flex items-center justify-center hover:bg-[#8E8E93] transition-colors"
              >
                <X size={11} className="text-white" strokeWidth={3} />
              </button>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowFilter(v => !v)}
              className={`w-11 h-11 rounded-[12px] flex items-center justify-center transition-colors relative border shadow-sm ${
                showFilter || activeFilterCount > 0
                  ? 'bg-[#007AFF] text-white border-[#007AFF]'
                  : 'bg-white border-black/5 text-[#007AFF] hover:bg-black/5'
              }`}
              title="Фильтры"
            >
              <SlidersHorizontal size={20} strokeWidth={2} />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FF3B30] rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {showFilter && (
              <FilterDropdown
                filter={filter}
                setFilter={setFilter}
                allTags={allTags}
                onClose={() => setShowFilter(false)}
              />
            )}
          </div>
        </div>

        {/* Active filters chips */}
        {activeFilterCount > 0 && (
          <div className="flex gap-2 mt-3 max-w-4xl mx-auto w-full overflow-x-auto hide-scrollbar">
            {filter.onlineOnly && (
              <button
                onClick={() => setFilter({ ...filter, onlineOnly: false })}
                className="flex items-center gap-1.5 bg-[#34C759]/15 text-[#248A3D] text-[13px] font-medium px-3 py-1 rounded-full shrink-0"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#34C759]" />
                Онлайн
                <X size={12} strokeWidth={2.5} />
              </button>
            )}
            {filter.hasTickets && (
              <button
                onClick={() => setFilter({ ...filter, hasTickets: false })}
                className="flex items-center gap-1.5 bg-[#007AFF]/10 text-[#007AFF] text-[13px] font-medium px-3 py-1 rounded-full shrink-0"
              >
                Есть тикеты
                <X size={12} strokeWidth={2.5} />
              </button>
            )}
            {filter.tags.map(tag => (
              <button
                key={tag}
                onClick={() => setFilter({ ...filter, tags: filter.tags.filter(t => t !== tag) })}
                className="flex items-center gap-1.5 bg-black/8 text-black text-[13px] font-medium px-3 py-1 rounded-full shrink-0"
              >
                {tag}
                <X size={12} strokeWidth={2.5} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 md:p-6 max-w-4xl w-full mx-auto">
        {filteredUsers.length > 0 ? (
          <div className="bg-white rounded-[20px] shadow-sm border border-black/5 overflow-hidden">
            {filteredUsers.map((user, idx) => {
              const userTickets = tickets.filter(t => t.userId === user.id);
              const openTickets = userTickets.filter(t => t.status === 'open' || t.status === 'pending').length;
              return (
                <button
                  key={user.id}
                  onClick={() => setSelectedUserId(user.id)}
                  className={`w-full p-4 flex items-center gap-4 hover:bg-[#F9F9F9] transition-colors text-left ${idx !== filteredUsers.length - 1 ? 'border-b border-black/5' : ''}`}
                >
                  <Avatar name={`${user.firstName} ${user.lastName}`} size="48px" isOnline={user.isOnline} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-0.5">
                      <span className="text-[16px] font-semibold text-black truncate tracking-tight">
                        {user.firstName} {user.lastName}
                      </span>
                      <div className="flex items-center gap-2 ml-2 shrink-0">
                        {openTickets > 0 && (
                          <span className="text-[11px] font-bold bg-[#FF9500]/10 text-[#B26800] px-1.5 py-0.5 rounded-full">
                            {openTickets} акт.
                          </span>
                        )}
                        <span className="text-[13px] font-medium text-[#8E8E93]">{user.totalTickets} тик.</span>
                      </div>
                    </div>
                    <div className="text-[14px] text-[#8E8E93] truncate mb-1.5">{user.username}</div>
                    <div className="flex gap-1.5 overflow-hidden flex-wrap">
                      {user.tags.slice(0, 3).map(tag => (
                        <Pill key={tag} type="tag">{tag}</Pill>
                      ))}
                      {user.tags.length === 0 && <span className="text-[12px] text-black/30 italic">Нет тегов</span>}
                    </div>
                  </div>

                  <ChevronRight size={18} className="text-[#C7C7CC] shrink-0" strokeWidth={2} />
                </button>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-[20px] shadow-sm border border-black/5 p-12 flex flex-col items-center justify-center text-center">
            <Users size={44} className="text-[#E5E5EA] mb-4" strokeWidth={1.5} />
            <div className="text-[17px] font-semibold text-black mb-1">Никого не найдено</div>
            <div className="text-[15px] text-[#8E8E93]">
              {search ? 'Попробуйте изменить запрос' : 'Нет пользователей с такими фильтрами'}
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={() => setFilter({ onlineOnly: false, hasTickets: false, tags: [] })}
                className="mt-4 text-[#007AFF] font-medium text-[15px] hover:underline"
              >
                Сбросить фильтры
              </button>
            )}
          </div>
        )}
      </div>

      {/* User Profile Slide-in */}
      {selectedUserId && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSelectedUserId(null)} />
          <div className="relative w-full md:w-[380px] h-full bg-white shadow-2xl z-10 overflow-hidden">
            <UserProfile userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
          </div>
        </div>
      )}
    </div>
  );
};
